import { isValidElement, type ReactNode } from 'react';

export type AllOrNone<T> = T | { [K in keyof T]?: never };

export type NonEmptyArray<T> = [T, ...Array<T>];

export const isNonEmptyArray = <T>(array: Array<T>): array is NonEmptyArray<T> => {
  return array.length > 0;
};

export const hasNumber = (str: string | number | undefined) => /\d/.test(String(str));

// Properly check if the value is an "empty" value.
// This works better than lodash's isEmpty in that it properly handles
// number and boolean types.
// See https://medium.com/@trmaphi/lodash-isempty-value-you-might-be-using-it-the-wrong-way-d83210d7decf
export const isEmptyValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  Number.isNaN(value) ||
  (typeof value === 'object' && Object.keys(value).length === 0) ||
  (typeof value === 'string' && value.trim().length === 0);

export type WithLimitedKeys<T extends Record<string, T[string]>> = keyof T extends string
  ? {
      [K1 in keyof T]: K1 extends keyof T
        ? T[K1] extends Record<string, T[K1][never]>
          ? WithLimitedKeys<T[K1]>
          : T[K1]
        : never;
    }
  : never;

export type VoidValue = Promise<void> | void;

export function isReactNode(node: unknown): node is ReactNode {
  return isValidElement(node);
}

export interface AnyJsonObj {
  // biome-ignore lint/suspicious/noExplicitAny: No explanation was provided. Please be a good citizen and add one!
  [key: string]: any;
}

// Close to JSON:API compliant but the errors are not in an array.
export interface EhrHooksServiceError {
  data: {
    errors: {
      message: string;
    };
  };
}

export type JSONApiResponse<T, I = unknown> = {
  data?: Array<T>;
  included?: Array<I>;
  links?: { self: string; prev?: string; next?: string };
  errors?: Array<{ status: string; title: string; detail: string }>;
};

const JSONApiEndpointTypes = [
  'auth/roles',
  'auth/users',
  'auth/app-clients',
  'patient-history/jobs',
  'zap-data-subscriptions/enrollment-status',
  'zap-data-subscriptions/package',
  'patient-data-subscriptions/enrollment-status',
] as const;

type JSONApiEndpointType = (typeof JSONApiEndpointTypes)[number];

export type JSONApiBase = {
  type: JSONApiEndpointType;
  id?: string;
  attributes: Record<string, string>;
  relationships: Record<string, string>;
};

export type Constructor<T> = new (...args: Array<unknown>) => T;
